import { z } from 'zod';

/** Whether a key reaches the whole resource or only the collections it lists. */
export const accessControlSchema = z.enum(['globalAccess', 'collectionAccess'], {
  error: "Invalid accessControl, should be 'globalAccess' | 'collectionAccess'",
});

/** Read & write, or read-only, on one collection. */
export const collectionOperationsSchema = z.enum(['rw', 'r']);

type AccessControlFields = {
  accessControl: z.infer<typeof accessControlSchema>;
  allowedOperations?: string;
  collections?: readonly { name: string }[];
};

/** Collection access needs at least one collection; global access needs its allowed operations. */
export const refineAccessControl = (data: AccessControlFields, ctx: z.RefinementCtx) => {
  if (data.accessControl === 'collectionAccess') {
    if (!data.collections?.length) {
      ctx.addIssue({ code: 'custom', message: 'At least one collection is required', path: ['collections'] });
    }
    return;
  }
  if (!data.allowedOperations) {
    ctx.addIssue({ code: 'custom', message: 'Allowed operations are required', path: ['allowedOperations'] });
  }
};

/** Replaces the collection at `index`, or appends it when there is no index. */
export const upsertCollectionAt = <T>(collections: readonly T[] | undefined, collection: T, index?: number): T[] => {
  const next = collections ? [...collections] : [];
  if (index === undefined) {
    next.push(collection);
  } else {
    next[index] = collection;
  }
  return next;
};

/** Drops the collection at `index`; an emptied list becomes `undefined`, which the form reads as "none". */
export const removeCollectionAt = <T>(collections: readonly T[] | undefined, index: number): T[] | undefined => {
  const next = (collections ?? []).filter((_collection, i) => i !== index);
  return next.length ? next : undefined;
};
